import React, { useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import type { Feature, Geometry } from 'geojson';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AssistantDirectionIcon from '@mui/icons-material/AssistantDirection';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import { DELETE_OBJECT_MUTATION, GET_MY_OBJECTS_QUERY } from '../../../gql';
import { useGoTo } from '../../GoToContext/useGoTo';
import { useSettings } from '../../context/SettingsContext/useSettings';
import { useWorkspace } from '../../context/WorkspaceContext/useWorkspace';
import {
  getParentTypeColor,
  isParentType,
} from '../../enums/parentObjectTypes';
import { FormattedMessage, useIntl } from 'react-intl';
import { useVocabulary } from '../../../i18n';
import { IconButton as DesignSystemIconButton } from '../../design-system';

interface ObjectRow {
  id: string;
  name?: string | null;
  type?: string | null;
  parentId?: string | null;
  geometry?: Geometry | null;
  position?: Geometry | null;
}

type ListEntry =
  | { kind: 'orphan'; object: ObjectRow }
  | { kind: 'group'; parent: ObjectRow; children: ObjectRow[] };

// Bucket the flat query result (already sorted updated_at DESC) into parent
// groups + orphans while preserving that order across both: a group's slot
// in the list is driven by the parent's own last-edit time, not by whether
// it has children, so editing/creating any top-level object moves it above
// older groups. Children are nested under their parent and don't get their
// own top-level slot. Children whose referenced parent isn't in the list
// (deleted, filtered) fall back to rendering as orphans so nothing
// disappears.
function buildEntries(objects: ObjectRow[]): ListEntry[] {
  const parentIds = new Set<string>();
  for (const o of objects) {
    if (isParentType(o.type)) parentIds.add(o.id);
  }

  const childrenByParent = new Map<string, ObjectRow[]>();
  for (const o of objects) {
    if (!isParentType(o.type) && o.parentId && parentIds.has(o.parentId)) {
      const arr = childrenByParent.get(o.parentId) ?? [];
      arr.push(o);
      childrenByParent.set(o.parentId, arr);
    }
  }

  const entries: ListEntry[] = [];
  for (const o of objects) {
    if (isParentType(o.type)) {
      entries.push({
        kind: 'group',
        parent: o,
        children: childrenByParent.get(o.id) ?? [],
      });
    } else if (!(o.parentId && parentIds.has(o.parentId))) {
      entries.push({ kind: 'orphan', object: o });
    }
  }
  return entries;
}

interface ObjectListProps {
  objects: ObjectRow[];
  loading?: boolean;
}

interface ObjectListItemsProps {
  entries: ListEntry[];
  selectedObject: string | null;
  onSelect: (o: ObjectRow) => void;
  onGoto: (e: React.MouseEvent<HTMLButtonElement>, o: ObjectRow) => void;
  onDelete: (e: React.MouseEvent<HTMLButtonElement>, id: string) => void;
}

// Best-effort centroid — server-side ST_Centroid stores a Point in `position`
// but geometry-only rows (or client-side drafts) can also hit this list, so
// fall back to averaging coordinates.
function centroidOf(o: ObjectRow): { lng: number; lat: number } | null {
  const pos: any = o.position;
  if (pos && pos.type === 'Point' && Array.isArray(pos.coordinates)) {
    return { lng: pos.coordinates[0], lat: pos.coordinates[1] };
  }
  const geom: any = o.geometry ?? pos;
  if (!geom || !geom.coordinates) return null;
  const coords: [number, number][] = [];
  const collect = (c: any) => {
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      coords.push([c[0], c[1]]);
    } else if (Array.isArray(c)) {
      c.forEach(collect);
    }
  };
  collect(geom.coordinates);
  if (!coords.length) return null;
  const lng = coords.reduce((s, c) => s + c[0], 0) / coords.length;
  const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  return { lng, lat };
}

function hasGotoTarget(o: ObjectRow): boolean {
  const pos: any = o.position;
  if (pos && pos.type === 'Point' && Array.isArray(pos.coordinates)) {
    return true;
  }
  const geom: any = o.geometry ?? pos;
  return !!(geom && geom.coordinates);
}

const visibilityButtonStyles = {
  p: 0,
  ml: 0.75,
  color: 'grey.600',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'grey.800',
  },
};

// Left gutter reserved for the parent→children rail so the hover background of
// list rows never covers it. Applied to every entry (including orphans) so
// text left-edges stay aligned.
const RAIL_GUTTER_PX = 10;
const RAIL_WIDTH = 2;
const RAIL_INSET = 10;
const ROW_PL = 1.5;
const CHILD_PL = 2.5;

const entryShellStyles = {
  position: 'relative' as const,
  pl: `${RAIL_GUTTER_PX}px`,
};

const railStyles = (color: string) => ({
  content: '""',
  position: 'absolute' as const,
  left: 0,
  top: `${RAIL_INSET}px`,
  bottom: `${RAIL_INSET}px`,
  width: `${RAIL_WIDTH}px`,
  borderRadius: `${RAIL_WIDTH}px`,
  backgroundColor: color,
  pointerEvents: 'none' as const,
});

interface RowProps {
  object: ObjectRow;
  selected: boolean;
  indent?: boolean;
  onSelect: (o: ObjectRow) => void;
  onGoto: (e: React.MouseEvent<HTMLButtonElement>, o: ObjectRow) => void;
  onDelete: (e: React.MouseEvent<HTMLButtonElement>, id: string) => void;
}

const ObjectRowItem = React.memo(function ObjectRowItem({
  object,
  selected,
  indent = false,
  onSelect,
  onGoto,
  onDelete,
}: RowProps) {
  const intl = useIntl();
  const getVocabularyLabel = useVocabulary();
  const typeLabel = getVocabularyLabel('objectType', object.type);
  return (
    <ListItem
      disablePadding
      secondaryAction={
        <Stack direction="row" alignItems="center" gap={0.25}>
          {hasGotoTarget(object) && (
            <DesignSystemIconButton
              density="compact"
              tone="neutral"
              variant="ghost"
              aria-label={intl.formatMessage({
                id: 'map.objects.goTo',
                defaultMessage: 'Go to object',
              })}
              onClick={(e) => onGoto(e, object)}
            >
              <AssistantDirectionIcon />
            </DesignSystemIconButton>
          )}
          <DesignSystemIconButton
            density="compact"
            tone="danger"
            variant="ghost"
            aria-label={intl.formatMessage({
              id: 'map.objects.delete',
              defaultMessage: 'Delete object',
            })}
            onClick={(e) => onDelete(e, object.id)}
          >
            <DeleteOutlineIcon />
          </DesignSystemIconButton>
        </Stack>
      }
    >
      <ListItemButton
        dense
        selected={selected}
        onClick={() => onSelect(object)}
        sx={{ pl: indent ? CHILD_PL : ROW_PL }}
      >
        <ListItemText
          primary={
            object.name ||
            intl.formatMessage({
              id: 'map.objects.unnamed',
              defaultMessage: 'Unnamed object',
            })
          }
          secondary={typeLabel || undefined}
          primaryTypographyProps={{ variant: 'body2' }}
          secondaryTypographyProps={{ variant: 'caption' }}
        />
      </ListItemButton>
    </ListItem>
  );
});

// Memoized so Settings visibility toggles only re-render the header, not ~N rows.
const ObjectListItems = React.memo(function ObjectListItems({
  entries,
  selectedObject,
  onSelect,
  onGoto,
  onDelete,
}: ObjectListItemsProps) {
  return (
    <List dense disablePadding>
      {entries.map((entry) => {
        if (entry.kind === 'orphan') {
          return (
            <Box key={entry.object.id} sx={entryShellStyles}>
              <ObjectRowItem
                object={entry.object}
                selected={selectedObject === entry.object.id}
                onSelect={onSelect}
                onGoto={onGoto}
                onDelete={onDelete}
              />
            </Box>
          );
        }
        const { parent, children } = entry;
        const color = getParentTypeColor(parent.type);
        return (
          <Box
            key={parent.id}
            sx={{
              ...entryShellStyles,
              ...(children.length > 0 && { '&::before': railStyles(color) }),
            }}
          >
            <ObjectRowItem
              object={parent}
              selected={selectedObject === parent.id}
              onSelect={onSelect}
              onGoto={onGoto}
              onDelete={onDelete}
            />
            {children.map((c) => (
              <ObjectRowItem
                key={c.id}
                object={c}
                selected={selectedObject === c.id}
                indent
                onSelect={onSelect}
                onGoto={onGoto}
                onDelete={onDelete}
              />
            ))}
          </Box>
        );
      })}
    </List>
  );
});

const ObjectList = ({ objects, loading = false }: ObjectListProps) => {
  const intl = useIntl();
  const {
    store: { selectedObject, objectsVisibleOnMap },
    setSelectedObject,
    toggleObjectsMapVisibility,
  } = useSettings();
  const { startEditObject, clearObjectDraft } = useWorkspace();
  const { goTo } = useGoTo();

  const [deleteObject] = useMutation(DELETE_OBJECT_MUTATION, {
    refetchQueries: [{ query: GET_MY_OBJECTS_QUERY }],
  });

  const handleGoto = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, o: ObjectRow) => {
      e.stopPropagation();
      const c = centroidOf(o);
      if (c) goTo(c.lng, c.lat);
    },
    [goTo]
  );

  const handleSelect = useCallback(
    (o: ObjectRow) => {
      const geometry = o.geometry ?? o.position ?? null;
      const feature: Feature | null = geometry
        ? {
            type: 'Feature',
            geometry,
            properties: { id: o.id },
          }
        : null;
      setSelectedObject(o.id);
      startEditObject(o.id, feature);
    },
    [setSelectedObject, startEditObject]
  );

  const handleDelete = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
      e.stopPropagation();
      if (
        !window.confirm(
          intl.formatMessage({
            id: 'map.objects.confirmDelete',
            defaultMessage: 'Delete this object?',
          })
        )
      ) {
        return;
      }
      if (selectedObject === id) {
        setSelectedObject(null);
        clearObjectDraft();
      }
      await deleteObject({ variables: { id } });
    },
    [selectedObject, setSelectedObject, clearObjectDraft, deleteObject, intl]
  );

  const entries = useMemo(() => buildEntries(objects ?? []), [objects]);
  const visibilityTooltip = intl.formatMessage({
    id: 'map.objects.visibilityTooltip',
    defaultMessage: 'Hides the objects in the map while keeping them loaded.',
  });

  if (loading) {
    return (
      <Typography variant="caption" color="text.secondary">
        <FormattedMessage
          id="map.objects.loading"
          defaultMessage="Loading objects…"
        />
      </Typography>
    );
  }

  if (!objects || objects.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        <FormattedMessage
          id="map.objects.empty"
          defaultMessage="No objects yet."
        />
      </Typography>
    );
  }

  return (
    <Stack>
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          <FormattedMessage
            id="map.objects.count"
            defaultMessage="Objects ({count})"
            values={{ count: objects.length }}
          />
        </Typography>
        <Tooltip arrow title={visibilityTooltip}>
          <IconButton
            size="small"
            aria-label={
              objectsVisibleOnMap
                ? intl.formatMessage({
                    id: 'map.objects.hideOnMap',
                    defaultMessage: 'Hide objects on map',
                  })
                : intl.formatMessage({
                    id: 'map.objects.showOnMap',
                    defaultMessage: 'Show objects on map',
                  })
            }
            onClick={toggleObjectsMapVisibility}
            sx={visibilityButtonStyles}
          >
            {objectsVisibleOnMap ? (
              <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
            ) : (
              <VisibilityOffOutlinedIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
      <ObjectListItems
        entries={entries}
        selectedObject={selectedObject}
        onSelect={handleSelect}
        onGoto={handleGoto}
        onDelete={handleDelete}
      />
    </Stack>
  );
};

export { ObjectList };
